import { isBareKey, type LookSlot } from "../rules/look-records.ts";
import type { DirectionName } from "./sheet.ts";

/**
 * The block layout every look sheet shares (ADR-094 decision 12), as plain serialisable data, and the names and the
 * drawing order of a look's sheets (decision 14). The converter (`tools/character-looks`) writes the layout as
 * `layout.json` beside one PNG per sheet, each the size of the block; a page composes a look by drawing its sheets,
 * in `LOOK_COMPOSE_ORDER`, at the same place in a free block of a look page, and the block's cells are then the
 * frames of one sprite.
 *
 * A body layer's piece (skin, outfit, hair, hat) is one sheet. A hand piece is one sheet per rank: each of its cells
 * is drawn in exactly one of them, the one whose rank the packs' layer order guides give the tool in that cell, so a
 * tool's order against the body and against the other tool needs no per-frame logic when a look is composed.
 */

/**
 * The layout's format version.
 */
export const LOOK_LAYOUT_VERSION = 1;

/**
 * The ranks a hand piece is drawn at, bottom first: behind the body and behind the other tool, behind the body and
 * in front of the other tool, and the same two in front of the body.
 */
export const LOOK_RANKS = ["behind-under", "behind-over", "front-under", "front-over"] as const;
export type LookRank = (typeof LOOK_RANKS)[number];

/**
 * The slots whose pieces are tools, cut into one sheet per rank.
 */
export const HAND_SLOTS: readonly LookSlot[] = ["mainHand", "offHand"];

/**
 * One layer of a composed look: a slot's sheet, and the rank of a hand piece's (null for a body layer).
 */
export interface LookComposeLayer {
  readonly slot: LookSlot;
  readonly rank: LookRank | null;
}

/**
 * The order a look's sheets are drawn in, bottom first: the tools behind, the skin, the outfit, the hair, the hat,
 * the tools in front. Where both tools hold one rank the main hand is drawn first, so the off hand is over it.
 */
export const LOOK_COMPOSE_ORDER: readonly LookComposeLayer[] = Object.freeze([
  { slot: "mainHand", rank: "behind-under" },
  { slot: "offHand", rank: "behind-under" },
  { slot: "mainHand", rank: "behind-over" },
  { slot: "offHand", rank: "behind-over" },
  { slot: "skin", rank: null },
  { slot: "outfit", rank: null },
  { slot: "hair", rank: null },
  { slot: "hat", rank: null },
  { slot: "mainHand", rank: "front-under" },
  { slot: "offHand", rank: "front-under" },
  { slot: "mainHand", rank: "front-over" },
  { slot: "offHand", rank: "front-over" },
] as const);

/**
 * One cell of the block: its rectangle in the block, and where that rectangle's top left corner sits relative to
 * the pivot, the point that stands on the character's position (negative is left of it and above it).
 */
export interface LookLayoutCell {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

/**
 * One strip: the rate it plays at and, for each direction, the cell of each frame as an index into the layout's
 * `cells`. A frame held longer repeats its cell.
 */
export interface LookLayoutStrip {
  readonly fps: number;
  readonly frames: Readonly<Record<DirectionName, readonly number[]>>;
}

/**
 * The layout: the block's size, the gutter kept clear around every cell, the cells, the strips by name, the ranks
 * and the drawing order as the sheets were cut for them, and how far the tallest hair a player may choose reaches
 * above the pivot on the standing strip, the one head height plates, bars and clicks use for every look
 * (decision 34).
 */
export interface LookLayout {
  readonly version: number;
  readonly block: { readonly width: number; readonly height: number };
  readonly gutter: number;
  readonly cells: readonly LookLayoutCell[];
  readonly strips: Readonly<Record<string, LookLayoutStrip>>;
  readonly ranks: readonly LookRank[];
  readonly compose: readonly LookComposeLayer[];
  readonly head: number;
}

/**
 * The sheets a piece is cut into, by id: a sheet's file is its id with `.png`.
 *
 * @param slot The piece's slot.
 * @param key The piece's key.
 * @returns The key alone for a body layer; for a hand piece, the key with each rank, bottom first; none for a bare
 *   piece (`isBareKey`: the hair that is none), so a converter cuts no sheet for it, a pointer names none and a page
 *   draws no layer.
 * @example
 * lookSheetIds("mainHand", "sw01-1");
 * // ["sw01-1.behind-under", "sw01-1.behind-over", "sw01-1.front-under", "sw01-1.front-over"]
 */
export function lookSheetIds(slot: LookSlot, key: string): string[] {
  if (isBareKey(key)) {
    return [];
  }
  return HAND_SLOTS.includes(slot) ? LOOK_RANKS.map((rank) => `${key}.${rank}`) : [key];
}
